import React, { HTMLAttributes, ReactChild } from 'react'

import { css } from '@emotion/core'

import { useTheme } from 'emotion-theming'

interface RadioButtonProps<T> extends HTMLAttributes<T> {
  field?: {}
  id?: string
  name?: string
  disabled?: boolean
  checked?: boolean
  label?: string
  onChange?: () => void
  onClick?: () => void
  children?: ReactChild
  labelPlacement?: 'end' | 'start'
}

const radioButtonStyle = (theme: ThemeType) => css`
  display: flex;
  width: fit-content;
  margin: 4px 0;
  padding: 8px;
  position: relative;
  cursor: pointer;
  &:hover {
    background-color: ${theme.gray.gray10};
    border-radius: 4px;
  }
  &:last-of-type {
    margin-bottom: 0;
  }
  input[type='radio'] {
    display: none;
  }
  .radio-button-checkmark {
    position: absolute;
    top: 8px;
    left: 8px;
    height: 24px;
    width: 24px;
    background-color: ${theme.neutral.white};
    border-radius: 50%;
    border: solid 1px ${theme.gray.gray30};
    cursor: pointer;
    &:after {
      content: '';
      position: absolute;
      display: none;
    }
  }
  input[type='radio']:checked ~ .radio-button-checkmark {
    background-color: ${theme.campus.aqua30};
    border: solid 1px ${theme.campus.aqua30};
  }
  input[type='radio']:checked ~ .radio-button-checkmark:after {
    display: block;
  }
  input[type='radio']:disabled ~ .radio-button-checkmark {
    border: solid 1px ${theme.gray.gray30};
    background-color: ${theme.gray.gray20};
    cursor: not-allowed;
  }
  input[type='radio']:checked:disabled ~ .radio-button-checkmark {
    background-color: ${theme.gray.gray20};
    cursor: not-allowed;
  }
  input[type='radio']:checked:disabled ~ .radio-button-checkmark:after {
    background-color: ${theme.gray.gray30};
  }
  .radio-button-checkmark:after {
    top: 6px;
    left: 6px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${theme.neutral.white};
  }
  .radio-button-label {
    margin-left: 40px;
    min-height: 24px;
    font-size: 14px;
    line-height: 21px;
    color: ${theme.gray.gray60};
  }
`

const radioButtonLabelStyle = css`
  width: inherit;
  padding: 16px 0;
  .radio-button-checkmark {
    right: 0;
    left: unset;
    top: 16px;
  }
  .radio-button-label {
    margin-left: 0;
  }
`

const RadioButton = (props: RadioButtonProps<{}>) => {
  const theme: ThemeType = useTheme()
  const { field, disabled, checked, onClick, children, label, onChange, labelPlacement = 'end', ...otherProps } = props
  return (
    <label css={[radioButtonStyle(theme), labelPlacement === 'start' && radioButtonLabelStyle]}>
      <input
        type="radio"
        className="radio-button"
        disabled={disabled || false}
        checked={checked || false}
        onChange={onChange}
        onClick={onClick}
        readOnly
        {...field}
        {...otherProps}
      />
      <span className="radio-button-checkmark"></span>
      {children ? children : <span className="radio-button-label">{label}</span>}
    </label>
  )
}

export default RadioButton
